import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  placeholderSrc?: string;
  className?: string;
  aspectRatio?: string;
  loadingStrategy?: 'lazy' | 'eager';
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  placeholderSrc = '/placeholder.svg',
  className,
  aspectRatio = 'aspect-video',
  loadingStrategy = 'lazy',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(placeholderSrc);
  
  useEffect(() => {
    // Create a new image object
    const img = new Image();
    
    // Set up handlers before setting src
    img.onload = () => {
      setImgSrc(src);
      setIsLoaded(true);
    };
    
    img.onerror = () => {
      console.error(`Failed to load image: ${src}`);
      // Keep placeholder on error
    };
    
    // Start loading the image
    img.src = src;
    
    return () => {
      // Cleanup
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);
  
  return (
    <div className={cn(
      aspectRatio,
      'overflow-hidden bg-gray-100 relative',
      className
    )}>
      <img
        src={imgSrc}
        alt={alt}
        loading={loadingStrategy}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-500',
          isLoaded ? 'opacity-100' : 'opacity-60'
        )}
        {...props}
      />
    </div>
  );
};

export default LazyImage;
